import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, Divider, message } from 'antd';
import { v4 as uuidv4 } from 'uuid';
import FollowUpItemTable from './FollowUpItemsTable';
import FollowUpItemForm from './FollowUpItemsForm';
import { getPricing } from '@app/api/nominalPayments.api';

interface FollowUpItemsProps {
  onItemsChange?: (items: { products: any[]; service_charges: any[] }) => void;
  departmentOptions: { label: string; value: string }[];
  followUpItemsData?: {
    products: any[];
    service_charges: any[];
  };
  editable?: boolean;
  type?: 'edit' | 'create' | 'view';
}

const FollowUpItems: React.FC<FollowUpItemsProps> = ({
  onItemsChange,
  departmentOptions,
  followUpItemsData,
  editable,
  type,
}) => {
  const [items, setItems] = useState<any[]>([]);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const hasManuallyAddedItems = useRef(false);
  console.log('passed type to Items: ', type);

  const calculateTotal = (item: any) => {
    if (item.department_id === 6) {
      // Services: no_of_qa * serviceChargePrice
      return (item.no_of_qa || 1) * (item.unit_price || 0);
    } else {
      // Products: qty * unit_price
      return (item.qty || 1) * (item.unit_price || 0);
    }
  };

  const transformExistingData = (followUpItemsData: any) => {
    const transformedItems: any[] = [];

    const transformItem = (item: any, itemType: 'product' | 'service_charges') => {
      // Only transform if item has the nested structure (from API)
      if (item.department && item.product_category) {
        const baseItem = {
          key: uuidv4(),
          department_id: item.department_id,
          departmentLabel: `${item.department.code} - ${item.department.name}`,
          qty: parseFloat(item.qty),
          no_of_qa: item?.no_of_qa || 1, // Default to 1 if not provided
          days: item.days || 0,
          unit_price: parseFloat(item.unit_price || '0'),
          originalItem: item,
          product_category: item.product_category?.name || '',
          product: item.product?.name || '',
          product_category_id: item.product_category?.id,
          product_id: item.product?.id,
        };

        if (itemType === 'product') {
          const productItem = {
            ...baseItem,
            product: item.product?.name || '',
            product_variety: item.product_variety?.name || '',
            product_id: item.product?.id,
            product_variety_id: item.product_variety?.id,
            productLabel: [item.product_category?.name, item.product?.name, item.product_variety?.name]
              .filter(Boolean)
              .join(' - '),
            layer: item.product_variety ? 'variety' : item.product ? 'species' : 'category',
            type: 'product',
            uom: item.uom || '',
            // Always try to use the API's total first
            total: item.total ? parseFloat(item.total) : calculateTotal(baseItem),
          };

          return productItem;
        } else {
          const serviceItem = {
            ...baseItem,
            service_category: item.product_category?.name || '',
            serviceCharge: item.service_charge ? `${item.service_charge.code} - ${item.service_charge.name}` : '-',
            serviceChargePrice: parseFloat(item.service_charge_price || '0'),
            productLabel: item.product_category?.name || '',
            layer: 'category',
            type: 'service',
            lcl: parseFloat(item.lcl) || 0,
            fcl: parseFloat(item.fcl) || 0,
            service_charge_id: item.service_charge_id,
            // Always try to use the API's total first
            total: item.total ? parseFloat(item.total) : calculateTotal(baseItem),
          };

          return serviceItem;
        }
      }
      return null;
    };

    // Transform products
    if (followUpItemsData?.products) {
      followUpItemsData.products.forEach((product: any) => {
        const transformedItem = transformItem(product, 'product');
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    // Transform services
    if (followUpItemsData?.service_charges) {
      followUpItemsData.service_charges.forEach((service: any) => {
        const transformedItem = transformItem(service, 'service_charges');
        if (transformedItem) {
          transformedItems.push(transformedItem);
        }
      });
    }

    return transformedItems;
  };

  // Helper function to separate items and notify parent
  const groupItemsByType = useCallback((items: any[]) => {
    const grouped = {
      products: [] as any[],
      service_charges: [] as any[],
    };

    items.forEach((item) => {
      if (item.department_id === 7) {
        grouped.products.push(item);
      } else if (item.department_id === 6) {
        grouped.service_charges.push(item);
      }
    });

    return grouped;
  }, []);

  // Initialize items with existing data only if available and not already loaded
  useEffect(() => {
    console.log('useEffect triggered:', {
      initialDataLoaded,
      hasFollowUpItemsData: !!followUpItemsData,
      productsLength: followUpItemsData?.products?.length,
      servicesLength: followUpItemsData?.service_charges?.length,
      hasManuallyAddedItems: hasManuallyAddedItems.current,
    });

    // Only run this effect once when followUpItemsData is first provided
    if (!initialDataLoaded && !hasManuallyAddedItems.current) {
      if (followUpItemsData && (followUpItemsData.products?.length || followUpItemsData.service_charges?.length)) {
        console.log('Loading initial data...');
        const transformedItems = transformExistingData(followUpItemsData);
        console.log('Transformed items:', transformedItems);
        setItems(transformedItems);
        // Also notify parent about the initial data
        onItemsChange?.(groupItemsByType(transformedItems));
      }
      setInitialDataLoaded(true);
    }
  }, [followUpItemsData]);

  const handleAddItem = (item: any) => {
    console.log('Adding item:', item);
    hasManuallyAddedItems.current = true; // Mark that we've manually added items
    const newItem = {
      ...item,
      key: uuidv4(),
      total: calculateTotal(item),
    };
    setItems((prevItems) => {
      const updatedItems = [...prevItems, newItem];
      console.log('Updated items:', updatedItems);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleRemoveItem = (key: string) => {
    console.log('Removing item:', key);
    setItems((prevItems) => {
      const updatedItems = prevItems.filter((item) => item.key !== key);
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);
      return updatedItems;
    });
  };

  const handleFieldUpdate = async (key: string, field: 'no_of_qa' | 'days', newValue: number) => {
    console.log('handleFieldUpdate called:', { key, field, newValue });

    try {
      const itemIndex = items.findIndex((item) => item.key === key);
      if (itemIndex === -1) {
        console.error('Item not found:', key);
        throw new Error('Item not found');
      }

      const item = items[itemIndex];
      console.log('Found item:', item);

      const updatedValues = {
        ...item,
        [field]: newValue,
      };

      if (item.department_id === 6) {
        console.log('Updating service item directly');
        updatedValues.total = calculateTotal(updatedValues);
        setItems((prevItems) => {
          const updatedItems = [...prevItems];
          updatedItems[itemIndex] = updatedValues;
          setTimeout(() => {
            onItemsChange?.(groupItemsByType(updatedItems));
          }, 0);
          return updatedItems;
        });
        return;
      }

      const pricingModel = item.department_id === 6 ? 'SERVICE_CHARGE' : item.department_id === 7 ? 'PRODUCT' : '';

      const pricingResponse = await getPricing(
        'FOLLOWUP',
        item.product_category_id,
        item.product_id,
        item.product_variety_id || null,
        item.department_id,
        field === 'days' ? newValue : item.days,
        field === 'no_of_qa' ? newValue : item.no_of_qa,
        pricingModel,
        null, // lab_service_id
        item.service_charge_id || null,
      );

      console.log('API response:', pricingResponse);
      const pricingData = pricingResponse?.data;

      if (!pricingData) {
        throw new Error('No pricing data received');
      }

      setItems((prevItems) => {
        const updatedItems = [...prevItems];
        const updatedItem = {
          ...updatedValues,
          qty: pricingData.qty,
          uom: pricingData.uom || '',
          unit_price: parseFloat(pricingData.unit_price || '0'),
        };

        updatedItem.total = calculateTotal(updatedItem);
        updatedItems[itemIndex] = updatedItem;

        setTimeout(() => {
          onItemsChange?.(groupItemsByType(updatedItems));
        }, 0);

        return updatedItems;
      });
    } catch (error) {
      console.error('Error in handleFieldUpdate:', error);
      throw error;
    }
  };

  const handleFieldChange = (key: string, field: 'lcl' | 'fcl', value: number) => {
    setItems((prevItems) => {
      const updatedItems = prevItems.map((item) => {
        if (item.key === key) {
          return {
            ...item,
            [field]: value,
            total: (item.unit_price || 0) * (field === 'lcl' || field === 'fcl' ? item.qty || 1 : 1),
          };
        }
        return item;
      });

      // Notify parent if needed
      setTimeout(() => {
        onItemsChange?.(groupItemsByType(updatedItems));
      }, 0);

      return updatedItems;
    });
  };

  return (
    <>
      {editable && (
        <>
          <Card title="Add Follow Up Items" size="small">
            <FollowUpItemForm onAdd={handleAddItem} departmentOptions={departmentOptions} />
          </Card>
          <Divider />
        </>
      )}

      <FollowUpItemTable
        data={items}
        onRemove={editable ? handleRemoveItem : undefined}
        editable={editable}
        type={type}
        onFieldUpdate={handleFieldUpdate}
        handleFieldChange={editable && type !== 'view' ? handleFieldChange : undefined}
      />
    </>
  );
};

export default FollowUpItems;
